import { useState } from 'react'
import * as Yup from 'yup'
import { Form, Formik, Field } from 'formik'
import { useHistory, useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { isEmpty } from 'lodash'

import Modal from 'components/Modal'
import TextBox from 'components/TextBox'
import ToggleButton from 'components/ToggleButton'
import AssignedItemsButton from 'components/AssignedItemsButton'

import {
  getCustomPaymentType,
  getIsCustomPaymentTypesLoading,
} from 'store/CustomPaymentTypes/selectors'
import {
  getCurrentVenueUuid,
  getCurrentVenue,
} from 'store/Venues/selector'
import {
  createCustomPaymentType,
  updateCustomPaymentType,
  deleteCustomPaymentType,
} from 'store/CustomPaymentTypes/actions'
import { getRevenueCenters } from 'store/RevenueCenters/selector'
import RevenueCenters from '../RevenueCenters'
import { handleCustomTendersFormSubmit } from 'components/CustomTenders/utils/handleCustomTendersFormSubmit'

const CustomTendersForm = () => {
  const [showValidationErrors, setShowValidationErrors] =
    useState(true)
  const [formStep, setFormStep] = useState(1)
  const history = useHistory()
  const dispatch = useDispatch()
  const { state: urlQueryParamsState } = useLocation()

  const customTenderUuid = urlQueryParamsState?.customTenderUuid

  const venueUuid = useSelector(getCurrentVenueUuid)
  const currentVenue = useSelector(getCurrentVenue)
  const customPaymentType = useSelector((state) =>
    getCustomPaymentType(state, customTenderUuid)
  )
  const isLoading = useSelector(getIsCustomPaymentTypesLoading)

  const {
    displayName,
    active,
    noop,
    taxable,
    uuid,
    revenueCenters,
    managerApproval,
  } = customPaymentType

  const isNewCustomTender = customTenderUuid === 'new'

  const modalTitle = isNewCustomTender
    ? 'Add Custom Tender'
    : 'Edit Custom Tender'

  const modalStepTitle =
    formStep === 1 ? modalTitle : 'Revenue Centers'

  const allRevenueCenters = useSelector(getRevenueCenters)
  const allRevenueCenterUUIDs = allRevenueCenters.map(
    (center) => center.uuid
  )

  const selectedRevenueCenterUUIDs = () => {
    switch (revenueCenters) {
      case null:
        return allRevenueCenterUUIDs
      case '':
      case undefined:
        return []
      default:
        return revenueCenters
    }
  }

  const initialValues = {
    displayName: displayName ?? '',
    taxable: taxable ?? true,
    active: active ?? false,
    managerApproval: managerApproval ?? false,
    noop: noop ?? true,
    revenueCenters: selectedRevenueCenterUUIDs(),
  }

  const validationSchema = Yup.object().shape({
    displayName: Yup.string().required('Required'),
  })

  const disableUpdateButton = (dirty, errors) => {
    const disableButton =
      !dirty || (showValidationErrors && !isEmpty(errors))

    return disableButton
  }
  const onCloseModal = () =>
    history.push({ state: { customTenderUuid: null } })

  const onSubmit = (values) => {
    const onCreateCustomPaymentType = (
      createCustomPaymentTypeData
    ) => {
      dispatch(
        createCustomPaymentType(createCustomPaymentTypeData)
      ).then((response) => {
        // if action payload is empty, then stay on the same page
        if (isEmpty(response?.payload)) return

        history.push({ state: { customTenderUuid: null } })
      })
    }

    const onUpdateCustomPaymentType = (
      updateCustomPaymentTypeData
    ) => {
      dispatch(
        updateCustomPaymentType(updateCustomPaymentTypeData)
      ).then((response) => {
        // if action payload is empty, then stay on the same page
        if (isEmpty(response?.payload)) return

        history.push({ state: { customTenderUuid: null } })
      })
    }

    handleCustomTendersFormSubmit(
      values,
      venueUuid,
      uuid,
      currentVenue?.organizationName,
      isNewCustomTender,
      onCreateCustomPaymentType,
      onUpdateCustomPaymentType
    )
  }

  return (
    <Modal
      className="-narrow"
      title={modalStepTitle}
      onClose={onCloseModal}
      isLoading={isLoading}
      onRequestClose={onCloseModal}
      isReturnIcon={formStep === 2}
      onClickReturn={() => setFormStep(1)}
    >
      <div className="modal-body">
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
        >
          {({ dirty, errors, values }) => (
            <Form className="custom-tender-settings">
              {formStep === 1 && (
                <div className="item-settings">
                  <div className="header-title">Info</div>
                  <div className="text-field">
                    <Field
                      component={TextBox}
                      label="Display Name"
                      name="displayName"
                      disabled={!isNewCustomTender}
                    />
                  </div>
                  <div className="content-wrapper -flags-wrapper">
                    <AssignedItemsButton
                      label="Revenue Centers"
                      assignedItems={values?.revenueCenters?.length}
                      handleClick={() => setFormStep(2)}
                    />
                  </div>
                  <div className="content-wrapper -flags-wrapper">
                    <span className="content-header">Active</span>
                    <span className="content-value">
                      <Field
                        component={ToggleButton}
                        name="active"
                        type="checkbox"
                      />
                    </span>
                  </div>
                  <div className="content-wrapper -flags-wrapper">
                    <span className="content-header">
                      Require Manager PIN
                    </span>
                    <span className="content-value">
                      <Field
                        component={ToggleButton}
                        name="managerApproval"
                        type="checkbox"
                      />
                    </span>
                  </div>
                  <div className="btn-wrapper">
                    <button
                      className="btn -update"
                      onClick={() => setShowValidationErrors(true)}
                      disabled={disableUpdateButton(dirty, errors)}
                      type="submit"
                    >
                      Save
                    </button>
                  </div>
                </div>
              )}
              {formStep === 2 && (
                <RevenueCenters
                  goBack={() => setFormStep(1)}
                  allRevenueCenters={allRevenueCenters}
                />
              )}
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  )
}

export default CustomTendersForm
